import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import type {
  InvoiceDetailDto,
  InvoiceSource,
  PurchaseOrderDetailDto,
  POLineItemDto,
} from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { formatAmount } from "@/lib/format";
import { validateAttachment } from "@/lib/attachment-validator";
import { parseDuplicateInvoiceError, type DuplicateInvoiceError } from "@/lib/duplicate-error";

// US-034 — Record Invoice form. MVP scope:
//   • Vendor invoice number + invoice / due date inputs.
//   • Line items pre-populated from the PO with editable qty + unit_price.
//   • Submit → POST /api/invoices → redirect to the new invoice detail.
//   • Add / remove AddOn lines wired in for parity with the BE contract;
//     qty-mismatch warnings + per-line addon approval land in their own
//     follow-up slices (US-045).
// US-035 — Source tagging + attachment upload. Source defaults to
//   InternalEntry; switching to VendorEmail nudges the user toward
//   attaching the vendor's original document (PDF / image / DOCX /
//   XLSX, up to 25 MB). Phase 1 stores attachment as a data URL on
//   the request body's attachment_url; Phase 2 will swap to presigned
//   uploads per ADR-001.

export const Route = createFileRoute("/invoices_/new")({
  validateSearch: (search: Record<string, unknown>) => ({
    po_id: typeof search.po_id === "string" ? search.po_id : "",
  }),
  head: () => ({
    meta: [
      { title: "Record Invoice — ProcureFlow" },
      { name: "description", content: "Record a vendor invoice against a purchase order." },
    ],
  }),
  component: InvoiceCreatePage,
});

interface LineDraft {
  key: string; // stable React key
  po_line_item_id: string | null;
  description: string;
  quantity: number;
  unit_price: number;
}

function lineKey(): string {
  // Stable per-render key; randomUUID is available in modern dev + test browsers.
  return crypto.randomUUID();
}

function InvoiceCreatePage() {
  const { po_id } = useSearch({ from: Route.id });
  const navigate = useNavigate();
  const [po, setPo] = useState<PurchaseOrderDetailDto | null>(null);
  const [poLoadError, setPoLoadError] = useState<string | null>(null);
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // US-036 duplicate-invoice error state — separate from `error` so the
  // form can render a clickable link to the existing invoice instead
  // of a plain text message.
  const [duplicate, setDuplicate] = useState<DuplicateInvoiceError | null>(null);
  // US-035 source + attachment state.
  const [source, setSource] = useState<InvoiceSource>("InternalEntry");
  const [attachmentName, setAttachmentName] = useState<string>("");
  const [attachmentDataUrl, setAttachmentDataUrl] = useState<string>("");
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);

  async function handleAttachmentChange(file: File | null) {
    setAttachmentError(null);
    if (!file) {
      setAttachmentName("");
      setAttachmentDataUrl("");
      return;
    }
    const validation = validateAttachment(file);
    if (!validation.ok) {
      setAttachmentError(validation.error);
      return;
    }
    setAttachmentLoading(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsDataURL(file);
      });
      setAttachmentName(file.name);
      setAttachmentDataUrl(dataUrl);
    } catch {
      setAttachmentError("Failed to read file.");
    } finally {
      setAttachmentLoading(false);
    }
  }

  useEffect(() => {
    if (!po_id) return;
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      try {
        const res = await fetchWithAuth(`/api/purchase-orders/${po_id}`);
        if (!res.ok) {
          if (!cancelled) setPoLoadError("Could not load the purchase order.");
          return;
        }
        const { data } = (await res.json()) as { data: PurchaseOrderDetailDto };
        if (cancelled) return;
        setPo(data);
        setLines(
          data.line_items.map((li: POLineItemDto) => ({
            key: lineKey(),
            po_line_item_id: li.id,
            description: li.description,
            quantity: li.quantity,
            unit_price: li.unit_price,
          })),
        );
      } catch {
        if (!cancelled) setPoLoadError("Could not load the purchase order.");
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [po_id]);

  function updateLine(idx: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }
  function addAddonLine() {
    setLines((prev) => [
      ...prev,
      {
        key: lineKey(),
        po_line_item_id: null,
        description: "",
        quantity: 1,
        unit_price: 0,
      },
    ]);
  }

  const total = lines.reduce((sum, l) => sum + l.quantity * l.unit_price, 0);

  const formReady =
    vendorInvoiceNumber.trim() !== "" &&
    invoiceDate !== "" &&
    dueDate !== "" &&
    dueDate >= invoiceDate &&
    lines.length > 0;

  async function handleSubmit() {
    if (!po) return;
    setSubmitting(true);
    setError(null);
    setDuplicate(null);
    try {
      const res = await fetchWithAuth("/api/invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          purchase_order_id: po.id,
          vendor_invoice_number: vendorInvoiceNumber.trim(),
          invoice_date: invoiceDate,
          due_date: dueDate,
          source,
          attachment_url: attachmentDataUrl || undefined,
          line_items: lines.map((l) => ({
            po_line_item_id: l.po_line_item_id,
            description: l.description,
            quantity: l.quantity,
            unit_price: l.unit_price,
          })),
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        data?: InvoiceDetailDto;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        // US-036 — 409 DUPLICATE_INVOICE carries the colliding invoice
        // id; surface a clickable link so the user can review the
        // existing record before retrying.
        const dup = parseDuplicateInvoiceError(body);
        if (dup) {
          setDuplicate(dup);
        } else {
          setError(body.message ?? "Could not save the invoice.");
        }
        return;
      }
      if (body.data) {
        navigate({ to: "/invoices/$id", params: { id: body.data.id } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!po_id) {
    return (
      <div className="space-y-6">
        <PageHeader title="Record Invoice" description="A purchase order is required." />
        <p className="text-sm text-muted-foreground">
          Open this page from a Purchase Order's "Record Invoice" action.
        </p>
      </div>
    );
  }

  if (poLoadError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Record Invoice" description="We couldn't load that PO." />
        <div className="rounded-2xl bg-card border border-border p-10 text-sm text-destructive shadow-elegant">
          {poLoadError}
        </div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="space-y-6">
        <PageHeader title="Record Invoice" description="Loading…" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Record Invoice"
        description={`Against ${po.po_number} · ${po.vendor.company_name}`}
        action={
          <Link
            to="/purchase-orders/$id"
            params={{ id: po.id }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to PO
          </Link>
        }
      />

      <form
        data-testid="invoice-create-form"
        className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (formReady && !submitting) void handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <Field
            id="vendor-invoice-number"
            label="Vendor invoice number"
            input={
              <input
                id="vendor-invoice-number"
                type="text"
                value={vendorInvoiceNumber}
                onChange={(e) => setVendorInvoiceNumber(e.target.value)}
                placeholder="e.g. VEND-2026-00123"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            }
          />
          <Field
            id="invoice-context"
            label="PO + vendor"
            input={
              <div className="text-sm text-muted-foreground">
                <span className="font-mono">{po.po_number}</span>
                <span className="px-2">·</span>
                <span>{po.vendor.company_name}</span>
              </div>
            }
          />
          <Field
            id="invoice-date"
            label="Invoice date"
            input={
              <input
                id="invoice-date"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            }
          />
          <Field
            id="due-date"
            label="Due date"
            input={
              <input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
              />
            }
          />
          {/* US-035: source + attachment. */}
          <Field
            id="invoice-source"
            label="Source"
            input={
              <select
                id="invoice-source"
                value={source}
                onChange={(e) => setSource(e.target.value as InvoiceSource)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
              >
                <option value="InternalEntry">Internal entry</option>
                <option value="VendorEmail">Vendor email</option>
              </select>
            }
          />
          <Field
            id="invoice-attachment"
            label={
              source === "VendorEmail"
                ? "Attach the vendor's invoice document"
                : "Attachment (optional)"
            }
            input={
              <div className="space-y-1.5">
                <input
                  id="invoice-attachment"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx,application/pdf,image/png,image/jpeg"
                  onChange={(e) => void handleAttachmentChange(e.target.files?.[0] ?? null)}
                  className="w-full text-xs text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-xs file:font-medium hover:file:bg-muted"
                />
                {attachmentLoading && (
                  <p className="text-xs text-muted-foreground">Reading file…</p>
                )}
                {attachmentName && !attachmentError && !attachmentLoading && (
                  <p className="text-xs text-muted-foreground">Attached: {attachmentName}</p>
                )}
                {attachmentError && (
                  <p role="alert" className="text-xs text-destructive">
                    {attachmentError}
                  </p>
                )}
              </div>
            }
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Line items
            </p>
            <button
              type="button"
              onClick={addAddonLine}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
            >
              <Plus className="h-3.5 w-3.5" />
              Add line
            </button>
          </div>

          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-right font-medium">Qty</th>
                  <th className="px-3 py-2 text-right font-medium">Unit price</th>
                  <th className="px-3 py-2 text-right font-medium">Line total</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, idx) => (
                  <tr key={line.key} className="border-t border-border">
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                      {line.po_line_item_id === null && (
                        <span className="mt-1 inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                          Add-on
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={line.quantity}
                        onChange={(e) =>
                          updateLine(idx, { quantity: parseInt(e.target.value || "0", 10) })
                        }
                        className="w-20 rounded-md border border-border bg-background px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={line.unit_price}
                        onChange={(e) =>
                          updateLine(idx, { unit_price: parseInt(e.target.value || "0", 10) })
                        }
                        className="w-28 rounded-md border border-border bg-background px-2 py-1 text-right text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">
                      {formatAmount(line.quantity * line.unit_price)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="text-muted-foreground hover:text-destructive"
                        aria-label="Remove line"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/20">
                  <td className="px-3 py-2 text-right text-xs uppercase tracking-wide" colSpan={3}>
                    Total
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    {formatAmount(total)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
        {duplicate && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs space-y-1"
          >
            <p className="text-destructive font-medium">{duplicate.message}</p>
            <p className="text-muted-foreground">
              The existing invoice is{" "}
              <Link
                to="/invoices/$id"
                params={{ id: duplicate.existingId }}
                className="text-primary underline hover:no-underline font-medium"
              >
                {duplicate.existingNumber}
              </Link>{" "}
              ({duplicate.existingDate}). Edit the original invoice or record under a corrected
              vendor invoice number.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Link
            to="/purchase-orders/$id"
            params={{ id: po.id }}
            className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={!formReady || submitting}
            className="inline-flex items-center rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
          >
            {submitting ? "Saving…" : "Save Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ id, label, input }: { id: string; label: string; input: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <div className="mt-1">{input}</div>
    </div>
  );
}
